import { DateTime } from 'luxon';
import { formatFullName } from './index';
import { DEFAULT_TIMEZONE, startOfToday } from './date.utils';
import {
  CHECK_IN_ATTENDANCE_MODES,
  CHECK_IN_METHODS,
  QR_TYPES,
} from '../constants/schedule.constants';

export function getHostIds(event) {
  return event?.attributes?.hostIds || (event?.attributes?.hostId ? [event.attributes.hostId] : []);
}

export function resolveHosts({ event, users }) {
  return getHostIds(event)
    .map(id => users.find(user => user.id === id))
    .filter(Boolean)
    .map(user => ({ id: user.id, name: formatFullName(user) }));
}

export function resolveHostName({ event, users }) {
  const names = resolveHosts({ event, users }).map(host => host.name);
  return names.length ? names.join(', ') : null;
}

// Host badge (does not elevate admins). For permission gating use isHostOrAdmin.
export function isCurrentUserHost({ event, userId }) {
  if (!userId) return false;
  return getHostIds(event).includes(userId);
}

/**
 * Whether an event is meant for the user, mirroring the backend's `userId` filter
 * on the events list: open to everyone (`openAttendance`, the default for
 * educational events, so a missing value reads as open), or listing the user as an
 * attendee. Hosts count too, since they run the event. No audience or group exists
 * on the event, so this is the closest signal to "events for your group".
 *
 * @param {{ event?: object, userId?: string | null }} params
 * @returns {boolean} False without a user id, so the filter never widens by accident.
 * @example
 * const mine = events.filter(event => isEventForUser({ event, userId: user.id }));
 */
export function isEventForUser({ event, userId }) {
  if (!userId || !event) return false;
  if (event.openAttendance !== false) return true;
  if ((event.attendees ?? []).some(attendee => String(attendee?.userId) === String(userId))) {
    return true;
  }
  return isCurrentUserHost({ event, userId });
}

/**
 * Orders check-ins for the attendee list: the current user's first, then newest
 * first. Check-ins without a time sort last. Returns a new array.
 *
 * @param {Array<{ userId: string, checkedInAt?: string }>} items
 * @param {string | null | undefined} currentUserId
 * @returns {Array<{ userId: string, checkedInAt?: string }>}
 * @example
 * const rows = sortCheckInsForList(items, user.id);
 */
export function sortCheckInsForList(items, currentUserId) {
  const time = item => {
    const value = item?.checkedInAt ? new Date(item.checkedInAt).getTime() : NaN;
    return Number.isNaN(value) ? 0 : value;
  };
  return [...(items ?? [])].sort((a, b) => {
    const aMine = Boolean(currentUserId) && a.userId === currentUserId;
    const bMine = Boolean(currentUserId) && b.userId === currentUserId;
    if (aMine !== bMine) return aMine ? -1 : 1;
    return time(b) - time(a);
  });
}

// Permission gating (elevates admins). For the host badge use isCurrentUserHost.
export function isHostOrAdmin({ event, userId, isAdmin }) {
  if (isAdmin) return true;
  if (!userId) return false;
  return getHostIds(event).includes(userId);
}

/**
 * Whether the event is synced from an external source, such as Epic. The next sync would
 * overwrite any edit made here, so the app offers none.
 *
 * @param {object | null | undefined} event - Any schedule event.
 * @returns {boolean} True when the event has an `externalRef`.
 * @example
 * const canEdit = isAdmin && !isExternallyManaged(event);
 */
export function isExternallyManaged(event) {
  return Boolean(event?.externalRef);
}

/**
 * Whether check-in is open now, as resolved by the backend (`event.checkIn.isOpen`,
 * which covers the scheduled window). Falls back to the raw host session for a
 * backend that predates `checkIn`.
 * @param {object | null | undefined} event
 * @returns {boolean}
 */
export function isCheckInSessionOpen(event) {
  if (typeof event?.checkIn?.isOpen === 'boolean') return event.checkIn.isOpen;
  const session = event?.attributes?.checkInSession;
  return Boolean(session?.openedAt && !session?.closedAt);
}

/**
 * The next scheduled check-in transitions (ISO strings): `opensAt` is set while
 * an open is coming, `closesAt` while a close is coming; null otherwise.
 * @param {object | null | undefined} event
 * @returns {{ opensAt: string | null, closesAt: string | null }}
 */
export function getCheckInSchedule(event) {
  return {
    opensAt: event?.checkIn?.opensAt ?? null,
    closesAt: event?.checkIn?.closesAt ?? null,
  };
}

// Single source of truth for the attendee "Check in" button's visibility on
// both platforms: the organizer opted in AND the host's check-in session is open.
export function canSelfAttest(event) {
  return event?.attributes?.allowSelfAttestation === true && isCheckInSessionOpen(event);
}

export function isStaticQr(event) {
  return event?.attributes?.qrType === QR_TYPES.STATIC;
}

export function getCheckInModeFromEvent({ event }) {
  const mode = event?.attributes?.attendanceMode;
  return CHECK_IN_ATTENDANCE_MODES.includes(mode) ? mode : null;
}

export function getCheckInMethodLabel({ method, lang }) {
  const labels = {
    [CHECK_IN_METHODS.QR]: lang.CHECK_IN_METHOD_QR,
    [CHECK_IN_METHODS.SELF_ATTESTATION]: lang.CHECK_IN_METHOD_SELF_ATTESTED,
    [CHECK_IN_METHODS.MANUAL]: lang.CHECK_IN_METHOD_MANUAL,
  };
  return labels[method] ?? '';
}

// Starts today or later (today-inclusive). isFutureEvent is strictly after now.
export function isUpcomingEvent(event) {
  if (!event?.startTime) return false;
  return new Date(event.startTime).getTime() >= startOfToday().getTime();
}

// Strictly after now. isUpcomingEvent is today-inclusive.
export function isFutureEvent(event) {
  if (!event?.startTime) return false;
  return new Date(event.startTime).getTime() > Date.now();
}

export function isPastEvent(event) {
  if (!event?.endTime) return false;
  return new Date(event.endTime).getTime() < Date.now();
}

export function isCheckInEligibleToday(event) {
  if (!event?.startTime || !event?.endTime) return false;
  const todayStart = startOfToday();
  const endOfToday = new Date(todayStart);
  endOfToday.setDate(endOfToday.getDate() + 1);
  const eventStart = new Date(event.startTime);
  const eventEnd = new Date(event.endTime);
  return eventEnd >= todayStart && eventStart < endOfToday;
}

/**
 * Calendar-day offset of the event's start vs today in the given IANA zone
 * (the org timezone — mirrors the old server-side gate on Organization.timezone).
 * 0 = today, >0 = future day, <0 = past day, null = unknown (missing/invalid input).
 * @param {{ event?: { startTime?: string }, timezone?: string }} params
 * @returns {number | null}
 */
export function getEventStartDayOffset({ event, timezone }) {
  if (!event?.startTime) return null;
  const zone = timezone || DEFAULT_TIMEZONE;
  const start = DateTime.fromISO(event.startTime, { zone }).startOf('day');
  if (!start.isValid) return null;
  const today = DateTime.now().setZone(zone).startOf('day');
  return Math.round(start.diff(today, 'days').days);
}
